/**
 * Cliente Axios centralizado do iTap URA.
 *
 * Responsabilidades:
 *   1. Injetar Authorization (`Bearer <jwt>` ou `Basic <base64>`) de acordo
 *      com o modo de autenticação ativo em `auth.store`.
 *   2. Injetar `X-Tenant-ID` automaticamente quando há um tenant ativo
 *      — exceto no modo `basic` (admin global, sem tenant).
 *   3. Refresh automático em 401: tenta uma única vez (flag `_retry`),
 *      atualiza o `accessToken` e repete a request original. Se o refresh
 *      falhar, limpa o store, redireciona para `/login` e mostra toast.
 *   4. Tratamento centralizado de erros: 403/409/422/429/5xx → toast Sonner
 *      com mensagens i18n. 422 é deixado passar para que o formulário possa
 *      mapear via `mapValidationErrors()`.
 *
 * Observações importantes:
 *   - As mensagens são lidas via `i18next.t()` para suportar i18n.
 *   - O redirect em 401 é feito via `window.location.assign('/login')` em
 *      vez de `useNavigate`, porque este módulo vive fora da árvore React.
 *   - O `mapValidationErrors` é exportado para uso direto em `onSubmit` dos
 *     formulários (React Hook Form `setError`).
 */

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

// Augmenta o tipo do Axios para incluir as flags internas do nosso cliente.
// Permite que services (ex.: auth.service.ts) passem `_skipErrorToast: true`
// na config sem precisar fazer cast — e mantém o tipo coerente com o que o
// response interceptor lê.
declare module 'axios' {
  interface AxiosRequestConfig {
    /** Marcado pelo response interceptor após uma tentativa de refresh.
     *  Garante que cada request seja repetida no máximo uma vez. */
    _retry?: boolean
    /** Quando `true`, o response interceptor não dispara toast automático
     *  para os status 403/409/429/500/502/503. Usado por chamadas que tratam
     *  erros localmente (ex.: formulário de login). */
    _skipErrorToast?: boolean
  }
}
import { toast } from 'sonner'

import i18n from '@/i18n'
import { config } from '@/lib/config'
import { API_TIMEOUT_MS, TENANT_HEADER } from '@/lib/constants'
import { useAuthStore } from '@/store/auth.store'
import { useTenantStore } from '@/store/tenant.store'
import type {
  ApiErrorResponse,
  PydanticError,
  TokenResponse,
} from '@/types/api'

// ───────────────────────────────────────────────────────────────────────────
// Axios instance
// ───────────────────────────────────────────────────────────────────────────

/**
 * Alias para o config interno do Axios já incluindo nossas flags `_retry`
 * e `_skipErrorToast` (declaradas via `declare module 'axios'` acima).
 */
type RetriableRequestConfig = InternalAxiosRequestConfig

export const api: AxiosInstance = axios.create({
  baseURL: config.apiUrl,
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// ───────────────────────────────────────────────────────────────────────────
// Request interceptor — Authorization + X-Tenant-ID
// ───────────────────────────────────────────────────────────────────────────

api.interceptors.request.use((reqConfig) => {
  const { mode, accessToken, basicCredentials } = useAuthStore.getState()
  const { activeTenant } = useTenantStore.getState()

  // 1. Authorization
  if (mode === 'jwt' && accessToken) {
    reqConfig.headers.set('Authorization', `Bearer ${accessToken}`)
  } else if (mode === 'basic' && basicCredentials) {
    reqConfig.headers.set('Authorization', `Basic ${basicCredentials}`)
  }

  // 2. X-Tenant-ID — só no modo jwt (admin basic não tem tenant)
  if (mode !== 'basic' && activeTenant) {
    reqConfig.headers.set(TENANT_HEADER, activeTenant.id)
  }

  return reqConfig
})

// ───────────────────────────────────────────────────────────────────────────
// Refresh token logic
// ───────────────────────────────────────────────────────────────────────────

/**
 * Promise compartilhada de refresh em curso — garante que múltiplas requests
 * 401 paralelas disparem apenas um POST /auth/refresh, e todas aguardem
 * o mesmo resultado.
 */
let refreshPromise: Promise<string> | null = null

/**
 * Faz POST /auth/refresh com o refresh_token persistido e atualiza o store.
 * Retorna o novo access_token. Lança se não houver refresh_token ou se o
 * servidor recusar.
 *
 * Usa uma instância axios "limpa" para evitar recursão pelo próprio
 * interceptor de response (que dispararia outro refresh em caso de 401 aqui).
 */
async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    const { refreshToken } = useAuthStore.getState()
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }

    const response = await axios.post<TokenResponse>(
      `${config.apiUrl}/api/v1/auth/refresh`,
      { refresh_token: refreshToken },
      {
        timeout: API_TIMEOUT_MS,
        headers: { 'Content-Type': 'application/json' },
      },
    )

    useAuthStore.getState().setJwtAuth(response.data)
    return response.data.access_token
  })()

  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Error handlers (toasts)
// ───────────────────────────────────────────────────────────────────────────

function t(key: string, defaultValue?: string): string {
  return i18n.t(key, { defaultValue: defaultValue ?? key, ns: 'common' })
}

/** Extrai a string de `detail` se for string; senão retorna fallback. */
function extractDetailString(data: unknown, fallback: string): string {
  const detail = (data as ApiErrorResponse | undefined)?.detail
  if (typeof detail === 'string' && detail.trim().length > 0) return detail
  return fallback
}

function handleErrorToast(error: AxiosError<ApiErrorResponse>): void {
  const status = error.response?.status
  const data = error.response?.data

  switch (status) {
    case 403:
      toast.error(t('errors.forbidden'))
      break
    case 409:
      toast.error(extractDetailString(data, t('errors.conflict')))
      break
    case 422:
      // Mensagens de validação são tratadas pelo formulário via
      // `mapValidationErrors()`. Aqui apenas garantimos UX caso não haja
      // form (ex: erro de validação numa mutação sem form acoplado).
      // O componente que chamou pode optar por suprimir esse toast lendo
      // `_skipErrorToast = true` no config da request.
      break
    case 429:
      toast.warning(t('errors.quotaExceeded'))
      break
    case 500:
      toast.error(t('errors.serverError'))
      break
    case 502:
      toast.error(t('errors.workerUnavailable'))
      break
    case 503: {
      const detail503 = (data as ApiErrorResponse | undefined)?.detail
      if (detail503 === 'TTS_ENGINE_NOT_XTTS') {
        toast.error(
          i18n.t('toasts.ttsEngineNotXtts', { ns: 'voiceSamples' }),
        )
      } else if (detail503 === 'IMPORT_NOT_CONFIGURED') {
        toast.error(
          i18n.t('errors.notConfigured', { ns: 'imports' }),
        )
      } else {
        toast.error(t('errors.noWorkerAvailable'))
      }
      break
    }
    default:
      // Erros de rede (sem response) — só logam para console (toast opcional).
      if (!error.response && error.code !== 'ERR_CANCELED') {
        toast.error(t('errors.network'))
      }
      break
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Response interceptor — 401 refresh + error toasts
// ───────────────────────────────────────────────────────────────────────────

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<ApiErrorResponse>) => {
    const originalRequest = error.config as RetriableRequestConfig | undefined

    // ─── 401: tentar refresh uma única vez ────────────────────────────────
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      useAuthStore.getState().mode === 'jwt' &&
      useAuthStore.getState().refreshToken
    ) {
      originalRequest._retry = true

      try {
        const newAccessToken = await refreshAccessToken()
        // Atualiza o header da request original e repete.
        originalRequest.headers.set(
          'Authorization',
          `Bearer ${newAccessToken}`,
        )
        return api(originalRequest)
      } catch {
        // Refresh falhou — limpa store, avisa o usuário e manda pro login.
        useAuthStore.getState().clearAuth()
        useTenantStore.getState().clearTenant()
        toast.error(t('errors.sessionExpired'))
        redirectToLogin()
        return Promise.reject(error)
      }
    }

    // ─── 401 sem refresh possível (modo basic ou sem refresh_token) ──────
    if (error.response?.status === 401) {
      useAuthStore.getState().clearAuth()
      useTenantStore.getState().clearTenant()
      toast.error(t('errors.sessionExpired'))
      redirectToLogin()
      return Promise.reject(error)
    }

    // ─── Demais erros: toast centralizado (a menos que suprimido) ────────
    if (!originalRequest?._skipErrorToast) {
      handleErrorToast(error)
    }

    return Promise.reject(error)
  },
)

/** Redireciona para a tela de login, evitando loops se já estivermos lá. */
function redirectToLogin(): void {
  if (typeof window === 'undefined') return
  if (window.location.pathname === '/login') return
  window.location.assign('/login')
}

// ───────────────────────────────────────────────────────────────────────────
// Form validation helpers
// ───────────────────────────────────────────────────────────────────────────

/**
 * Converte o array de erros 422 (Pydantic/FastAPI) em um map
 * `{ field: message }` pronto para alimentar React Hook Form `setError`.
 *
 * Usa o último elemento de `loc` como nome do campo, ignorando prefixos
 * como `"body"` ou índices de array (`["body", "items", 0, "name"]` → `"name"`).
 *
 * Spec §17:
 * ```ts
 * function mapValidationErrors(errors: PydanticError[]): Record<string, string> {
 *   return Object.fromEntries(errors.map(e => [e.loc[e.loc.length - 1], e.msg]))
 * }
 * ```
 */
export function mapValidationErrors(
  errors: PydanticError[],
): Record<string, string> {
  return Object.fromEntries(
    errors
      .filter((e) => Array.isArray(e.loc) && e.loc.length > 0)
      .map((e) => [String(e.loc[e.loc.length - 1]), e.msg]),
  )
}

/**
 * Extrai o array de PydanticError de um AxiosError 422, ou retorna `null`
 * se a resposta não estiver no formato esperado.
 *
 * Uso típico em `onError` de uma mutation:
 * ```ts
 * onError: (err) => {
 *   const validation = extractValidationErrors(err)
 *   if (validation) {
 *     Object.entries(mapValidationErrors(validation)).forEach(([field, msg]) =>
 *       form.setError(field, { message: msg }))
 *   }
 * }
 * ```
 */
export function extractValidationErrors(
  error: unknown,
): PydanticError[] | null {
  if (!axios.isAxiosError(error)) return null
  if (error.response?.status !== 422) return null
  const detail = (error.response.data as ApiErrorResponse | undefined)?.detail
  if (!Array.isArray(detail)) return null
  return detail as PydanticError[]
}

// ───────────────────────────────────────────────────────────────────────────
// Re-exports úteis
// ───────────────────────────────────────────────────────────────────────────

export type { AxiosRequestConfig, AxiosResponse, AxiosError }
export { axios }
export default api
